import { createEffect } from 'effector';

import { apiFetch, apiFetchV2 } from '@m/utils/http';

import type { Location, SiteOpsInfoForm, SiteOpsInfoView, UpdateHoursPayload } from '@m/types/api';

// Maps display status strings to the integer values expected by the API
// (matches SiteStatus IntEnum on the backend: Onboarding=3, Active=4, Inactive=5)
const SITE_STATUS_API_VALUES: Record<string, number> = {
  Onboarding: 3,
  Active: 4,
  Inactive: 5,
};

export const getLocations = createEffect({
  handler: ({
    searchQuery = null,
    siteStatuses,
  }: {
    searchQuery?: string | null;
    siteStatuses?: string[];
  } = {}) =>
    apiFetch('/admin/location-list', {
      method: 'GET',
      params: {
        search: searchQuery,
        pageSize: 2000,
        ...(siteStatuses?.length
          ? {
              statuses: siteStatuses
                .map((s) => SITE_STATUS_API_VALUES[s])
                .filter((v): v is number => v !== undefined),
            }
          : {}),
      },
    }),
});

export const getLocationDetail = createEffect({
  handler: ({ id }: { id: number }) => apiFetch(`/admin/location/${id}/detail`),
});

export const updateLocation = createEffect({
  handler: ({ id, location }: { id: number; location: Location }) =>
    apiFetch(`/admin/location/${id}/detail`, {
      method: 'PUT',
      body: location,
    }),
});

export const getLocationOpsInfo = ({ id }: { id: number }) =>
  apiFetchV2<SiteOpsInfoView>(`/admin/location/${id}/ops-info`);

// PATCH semantics: keys absent from `form` are left unchanged; explicit `null`
// clears the field on the backend.
export const updateLocationOpsInfo = ({ id, form }: { form: SiteOpsInfoForm; id: number }) =>
  apiFetchV2<SiteOpsInfoView>(`/admin/location/${id}/ops-info`, {
    method: 'PATCH',
    body: form,
  });

export const updateLocationHours = createEffect({
  handler: ({ id, payload }: { id: number; payload: UpdateHoursPayload }) =>
    apiFetch(`/admin/location/${id}/hours`, {
      method: 'POST',
      body: payload,
    }),
});

export const getLocationJoinQrCode = createEffect(
  ({ id, refreshImage }: { id: number; refreshImage?: boolean }) =>
    apiFetch<QrCode>(`/qr/site/${id}/get-or-create${refreshImage ? '?refreshImage=true' : ''}`, {
      method: 'POST',
    }),
);

export type QrCode = {
  content: string;
  qrCodePngUploadUrl: string;
  qrCodeSvgUploadUrl: string;
};
